import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Button, Collapse } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { PermissionArea, Text } from '@pbt/pbt-ui-components'
import { Warning as WarningIcon } from '@pbt/pbt-ui-components/src/icons'

import { getCRUDByArea } from '~/store/reducers/auth'

import { useNeedsLicenseEntry } from './hooks/useNeedsLicenseEntry'
import type { PrescriptionBodyProps } from './PrescriptionBody'

const useStyles = makeStyles(
  (theme) => ({
    warning: {
      color: 'white',
      backgroundColor: theme.colors.alertLabelError,
      display: 'inline-flex',
      alignItems: 'center',
      borderRadius: 3,
      gap: theme.spacing(1),
    },
    warningIcon: {
      width: '1.4rem',
      height: '1.4rem',
    },
    button: {
      textDecoration: 'underline',
      color: 'inherit',
      padding: 0,
      textTransform: 'initial',
      fontWeight: 'inherit',
      fontSize: 'inherit',
      minWidth: 'auto',
      lineHeight: 'inherit',
      verticalAlign: 'inherit',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  }),
  { name: 'DoctorLicenseWarning' },
)

interface DoctorLicenseWarningPros extends Pick<
  PrescriptionBodyProps,
  'currentDoctorHasDvmLicense'
> {
  doctorId: string
}

const DoctorLicenseWarning = ({
  currentDoctorHasDvmLicense,
  doctorId,
}: DoctorLicenseWarningPros) => {
  const classes = useStyles()
  const navigate = useNavigate()
  const { t } = useTranslation(['Common'])

  const personPermissions = useSelector(getCRUDByArea(PermissionArea.PERSON))
  // Use the same hook as button disabling - warning shows when this returns true
  // Note: isDvmLicenseRequired is always true when this component is rendered
  const isWarningVisible = useNeedsLicenseEntry(
    true, // isDvmLicenseRequired - always true when DoctorLicenseWarning is rendered
    currentDoctorHasDvmLicense,
    null, // currentDoctorHasMatchingStateLicense - not needed for DVM warning visibility
  )

  const navigateToAdminLicensesSection = () => {
    navigate(`/admin/general/members/${doctorId}`)
  }

  const isHidden = !isWarningVisible || !doctorId

  return (
    <Collapse unmountOnExit in={!isHidden}>
      <Text
        strong
        className={classes.warning}
        mb={2}
        px={1}
        py={0.25}
        variant="body2"
      >
        <WarningIcon className={classes.warningIcon} />
        <span>{t('Common:NO_LICENSE_ON_FILE')} </span>
        {personPermissions.update && (
          <Button
            className={classes.button}
            size="small"
            onClick={navigateToAdminLicensesSection}
          >
            {t('Common:ADD_NOW')}
          </Button>
        )}
      </Text>
    </Collapse>
  )
}

export default DoctorLicenseWarning
